/** Idade e ala do paciente: cálculo único usado por todas as telas do Portal. */
const SP_TODAY = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [y, m, d] = parts.split("-").map(Number);
  return { y, m, d };
};

export const computeAge = (birthDate) => {
  const raw = String(birthDate || "").slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return null;
  const [, by, bm, bd] = match.map(Number);
  const probe = new Date(Date.UTC(by, bm - 1, bd));
  if (probe.getUTCFullYear() !== by || probe.getUTCMonth() + 1 !== bm || probe.getUTCDate() !== bd) return null;
  const today = SP_TODAY();
  if (by > today.y || (by === today.y && (bm > today.m || (bm === today.m && bd > today.d)))) return null;
  let age = today.y - by;
  if (today.m < bm || (today.m === bm && today.d < bd)) age -= 1;
  return age < 0 ? null : age;
};

export const ageLabel = (patient) => {
  const birth = patient?.birth_date;
  if (!birth) return patient?.age_label || "Idade não informada";
  const age = computeAge(birth);
  if (age === null) return "Idade não informada";
  if (age < 1) return "Menos de 1 ano";
  return `${age} anos`;
};

/** Chip de idade para blocos de identificação do paciente. */
export const AgeChip = ({ patient, testId, className = "" }) => (
  <span
    data-testid={testId}
    className={`inline-flex shrink-0 items-center rounded-full bg-sand px-2.5 py-0.5 text-[12.5px] font-semibold text-ink-700 ${className}`}
  >
    {ageLabel(patient)}
  </span>
);

export const wardLabel = (patient) => {
  const text = String(patient?.ward || "").trim();
  if (!text) return "Sem ala definida";
  return patient?.ward_label || text.replace(/\s+/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());
};

export const WARD_ALL = "all";
export const PATIENT_WARDS = ["Masculina", "Feminina"];

/** Ala persistida do paciente convertida na opção exata do select (aceita MASCULINA, masculina etc.). */
export const wardOption = (value) => {
  const key = String(value || "").trim().toUpperCase();
  return PATIENT_WARDS.find((w) => w.toUpperCase() === key) || "";
};
export const STAFF_WARDS = ["Masculina", "Feminina", "Ambas"];

/** Filtro de ala inicial conforme a ala do funcionário logado ("Ambas" ou sem ala = todas). */
export const defaultWardFilter = (user) => {
  const ward = String(user?.ward || "").trim();
  if (!ward || ward.toLowerCase() === "ambas") return WARD_ALL;
  return ward.toUpperCase();
};

/** Ala obrigatória do funcionário logado: quando travada, o filtro de ala não é exibido. */
export const wardScope = (user) => {
  const ward = String(user?.ward || "").trim().toUpperCase();
  const locked = ward === "MASCULINA" || ward === "FEMININA";
  return {
    locked,
    value: locked ? ward : WARD_ALL,
    label: locked ? `Ala ${ward.charAt(0)}${ward.slice(1).toLowerCase()}` : "Todas as alas",
  };
};

/** Máscara de CPF XXX.XXX.XXX-XX aplicada durante a digitação e ao colar. */
export const formatCpf = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const cpfDigits = (value) => String(value || "").replace(/\D/g, "").slice(0, 11);
